
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNodes } from '@/hooks/useNodes';
import { useCreateThematicTrail, useThematicTrailItems } from '@/hooks/useNodes';
import { Plus, ArrowDown, X, Save, Eye } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface TrailBuilderProps {
  onTrailCreated?: () => void;
}

interface TrailItem {
  nodeId: string;
  orderIndex: number;
  title: string;
  disciplineName: string;
}

const TrailBuilder: React.FC<TrailBuilderProps> = ({ onTrailCreated }) => {
  const [trailTitle, setTrailTitle] = useState('');
  const [trailDescription, setTrailDescription] = useState('');
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [trailItems, setTrailItems] = useState<TrailItem[]>([]);
  const [filterDiscipline, setFilterDiscipline] = useState('all');

  const { data: allNodes } = useNodes();
  const createTrail = useCreateThematicTrail();

  const publishedNodes = allNodes?.filter(node => node.status === 'published') || [];
  const availableNodes = publishedNodes.filter(node => 
    !trailItems.some(item => item.nodeId === node.id) &&
    (filterDiscipline === 'all' || node.discipline_id === filterDiscipline)
  );

  const disciplines = Array.from(new Set(
    publishedNodes.map(node => node.disciplines?.name).filter(Boolean)
  ));

  const addNodeToTrail = () => {
    if (!selectedNodeId) return;
    
    const selectedNode = publishedNodes.find(node => node.id === selectedNodeId);
    if (!selectedNode) return;

    const newItem: TrailItem = {
      nodeId: selectedNodeId,
      orderIndex: trailItems.length,
      title: selectedNode.title,
      disciplineName: selectedNode.disciplines?.name || 'Unknown'
    };

    setTrailItems(prev => [...prev, newItem]);
    setSelectedNodeId('');
  };

  const removeItem = (index: number) => {
    setTrailItems(prev => prev.filter((_, i) => i !== index));
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === trailItems.length - 1)
    ) return;

    const newItems = [...trailItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    // Update order indices
    newItems.forEach((item, i) => {
      item.orderIndex = i;
    });
    
    setTrailItems(newItems);
  };

  const saveTrail = async () => {
    if (!trailTitle.trim()) {
      toast({ title: 'Error', description: 'Trail title is required', variant: 'destructive' });
      return;
    }

    if (trailItems.length === 0) {
      toast({ title: 'Error', description: 'At least one node is required', variant: 'destructive' });
      return;
    }

    try {
      await createTrail.mutateAsync({
        title: trailTitle,
        description: trailDescription,
        status: 'draft'
      });

      toast({ title: 'Success', description: 'Thematic trail created successfully' });
      
      // Reset form
      setTrailTitle('');
      setTrailDescription('');
      setTrailItems([]);
      
      onTrailCreated?.();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create trail', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Build New Thematic Trail
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Trail Title</label>
            <Input
              placeholder="Enter trail title..."
              value={trailTitle}
              onChange={(e) => setTrailTitle(e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <Input
              placeholder="Describe this learning path..."
              value={trailDescription}
              onChange={(e) => setTrailDescription(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Add Nodes to Trail</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by discipline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All disciplines</SelectItem>
                {disciplines.map((discipline) => (
                  <SelectItem key={discipline} value={discipline}>
                    {discipline}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedNodeId} onValueChange={setSelectedNodeId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select a node to add..." />
              </SelectTrigger>
              <SelectContent>
                {availableNodes.map((node) => (
                  <SelectItem key={node.id} value={node.id}>
                    {node.title} ({node.disciplines?.name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button onClick={addNodeToTrail} disabled={!selectedNodeId}>
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Trail Preview ({trailItems.length} nodes)</span>
            {trailItems.length > 0 && (
              <Button onClick={saveTrail} disabled={createTrail.isPending}>
                <Save className="h-4 w-4 mr-1" />
                {createTrail.isPending ? 'Creating...' : 'Create Trail'}
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {trailItems.length === 0 ? (
            <p className="text-black text-center py-8">
              No nodes added yet. Add nodes above to build your trail.
            </p>
          ) : (
            <div className="space-y-3">
              {trailItems.map((item, index) => (
                <div key={item.nodeId} className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                    {index + 1}
                  </div>
                  
                  <div className="flex-1">
                    <div className="font-medium">{item.title}</div>
                    <div className="text-sm text-black">{item.disciplineName}</div>
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === trailItems.length - 1}
                    >
                      ↓
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => removeItem(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrailBuilder;
